import type { FrontendNotebook } from '../contexts/NotebooksContext';

export type DraftNotebookKind = 'article' | 'recipe' | 'movie' | 'event';

/** Must match `PORTAL_DRAFT_MAGIC` in portalDraftEnvelope.ts (avoid circular import). */
const PORTAL_DRAFT_MAGIC = 'portal-draft:v1\n';

function kindFromPortalDraftContent(raw: string): DraftNotebookKind | null {
  if (!raw || typeof raw !== 'string' || !raw.startsWith(PORTAL_DRAFT_MAGIC)) return null;
  try {
    const parsed = JSON.parse(raw.slice(PORTAL_DRAFT_MAGIC.length)) as {
      schemaVersion?: number;
      kind?: string;
      payload?: unknown;
    };
    if (parsed?.schemaVersion !== 1 || !parsed.kind || parsed.payload == null) return null;
    const k = parsed.kind;
    if (k === 'article' || k === 'recipe' || k === 'movie' || k === 'event') return k;
    return null;
  } catch {
    return null;
  }
}

const STORAGE_KEY = 'bsm.notebookDraftKinds.v1';
const PENDING_OPEN_KEY = 'bsm.pendingOpenNotebookId';

export function rememberNotebookToOpen(notebookId: string): void {
  try {
    sessionStorage.setItem(PENDING_OPEN_KEY, notebookId);
  } catch {
    /* private mode */
  }
}

export function peekNotebookToOpen(): string | null {
  try {
    return sessionStorage.getItem(PENDING_OPEN_KEY);
  } catch {
    return null;
  }
}

export function clearNotebookToOpen(): void {
  try {
    sessionStorage.removeItem(PENDING_OPEN_KEY);
  } catch {
    /* private mode */
  }
}

function loadMap(): Record<string, DraftNotebookKind> {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as Record<string, string>;
    const out: Record<string, DraftNotebookKind> = {};
    for (const [id, v] of Object.entries(parsed)) {
      if (v === 'article' || v === 'recipe' || v === 'movie' || v === 'event') {
        out[id] = v;
      }
    }
    return out;
  } catch {
    return {};
  }
}

function saveMap(map: Record<string, DraftNotebookKind>) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(map));
  } catch {
    /* ignore quota */
  }
}

export function getStoredNotebookDraftKind(notebookId: string): DraftNotebookKind | null {
  return loadMap()[notebookId] ?? null;
}

export function setNotebookDraftKind(notebookId: string, kind: DraftNotebookKind): void {
  const m = loadMap();
  m[notebookId] = kind;
  saveMap(m);
}

export function removeNotebookDraftKind(notebookId: string): void {
  try {
    const m = loadMap();
    if (!(notebookId in m)) return;
    delete m[notebookId];
    saveMap(m);
  } catch {
    /* ignore */
  }
}

/**
 * Resolves Library draft kind: portal envelope in `draft.content` is the source of truth (API / seed-demo),
 * then optional local override, then legacy demo id prefixes, else article.
 */
export function inferNotebookDraftKind(notebook: FrontendNotebook): DraftNotebookKind {
  const fromEnvelope = kindFromPortalDraftContent(notebook.draft?.content?.trim() || '');
  if (fromEnvelope) return fromEnvelope;
  const stored = getStoredNotebookDraftKind(notebook.id);
  if (stored) return stored;
  if (notebook.id.startsWith('demo-notebook-recipes')) return 'recipe';
  if (notebook.id.startsWith('demo-notebook-movies')) return 'movie';
  if (notebook.id.startsWith('demo-notebook-events')) return 'event';
  if (notebook.id.startsWith('demo-notebook-published')) return 'article';
  return 'article';
}

export function mainDraftTabToKind(tabIndex: number): DraftNotebookKind | null {
  switch (tabIndex) {
    case 0:
      return 'article';
    case 2:
      return 'recipe';
    case 3:
      return 'movie';
    case 4:
      return 'event';
    default:
      return null;
  }
}

/**
 * Library “Articles” is backed by the same notebook entity as other draft kinds, but
 * {@link inferNotebookDraftKind} defaults unknown notebooks to `article`. This helper
 * keeps the Articles tab to rows that actually have an article workspace: a draft body
 * / publishing extras, or clusters used for article drafting — not bare empty notebooks.
 */
export function qualifiesAsLibraryArticleListItem(notebook: FrontendNotebook): boolean {
  const d = notebook.draft;
  const hasClusters =
    (notebook.cluster_ids?.length ?? 0) > 0 ||
    (Array.isArray(notebook.clusters) && notebook.clusters.length > 0);

  if (d) {
    const raw = (d.content || '').trim();
    if (raw.length > 0) return true;
    if (d.ready_for_publishing) return true;
    if (d.contrasting_views && d.contrasting_views.length > 0) return true;
    if (d.social_media_posts && d.social_media_posts.length > 0) return true;
    if (d.suggested_titles && d.suggested_titles.length > 0) return true;
    if (d.images && d.images.length > 0) return true;
    const t = (d.title || '').trim();
    if (t.length > 0 && t !== 'Untitled article') return true;
  }

  if (hasClusters) return true;

  return false;
}
